import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

/**
 * An agent row. Mirrors EXACTLY the shape returned by GET /agents
 * (apps/api/src/routes/agents/list.ts → toAgentResponse /
 * contracts AgentResponseSchema): there is no separate displayName column —
 * `extension` is the human-facing label.
 */
export interface AgentRow {
  id: string
  userId: string
  extension: string
  role: string
  maxConcurrentCalls: number
  status: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

interface AgentsResponse {
  agents: AgentRow[]
  total: number
  limit: number
  offset: number
}

export interface UseAgentsFilter {
  /** 'active' (default) excludes soft-deleted agents; 'all' includes them. */
  status?: 'active' | 'all'
}

export interface UseAgentsReturn {
  agents: AgentRow[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

// Module-level stable fallback — avoids a fresh [] reference each render
// (zustand/selector infinite-loop pitfall analog for hook consumers).
const EMPTY_AGENTS: AgentRow[] = []

const STALE_MS = 60 * 1000
const cache = new Map<string, { items: AgentRow[]; fetchedAt: number }>()

function pathFor(filter: UseAgentsFilter): string {
  // GET /agents only supports includeDeleted/role/search — there is no
  // ?status= param. 'active' = exclude deleted (default), 'all' = include.
  return filter.status === 'all' ? '/agents?includeDeleted=true' : '/agents'
}

/**
 * Fetches the tenant's agents from GET /agents. Module-level cache (60s TTL)
 * keyed by filter so transfer/co-agent dialogs don't refetch on every open.
 */
export function useAgents(
  filter: UseAgentsFilter = { status: 'active' },
): UseAgentsReturn {
  const path = pathFor(filter)
  const cached = cache.get(path)
  const [agents, setAgents] = useState<AgentRow[]>(cached?.items ?? EMPTY_AGENTS)
  const [loading, setLoading] = useState(cached === undefined)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<AgentsResponse>(path)
      const items = resp.agents ?? EMPTY_AGENTS
      cache.set(path, { items, fetchedAt: Date.now() })
      setAgents(items)
    } catch (e) {
      setError((e as Error).message)
      setAgents(EMPTY_AGENTS)
    } finally {
      setLoading(false)
    }
  }, [path])

  useEffect(() => {
    const c = cache.get(path)
    if (c !== undefined && Date.now() - c.fetchedAt <= STALE_MS) {
      setAgents(c.items)
      setLoading(false)
      return
    }
    void load()
  }, [path, load])

  return { agents, loading, error, refetch: load }
}

/** Test-only: clears the module-level cache. */
export function __resetAgentsCache(): void {
  cache.clear()
}
